/**
 * Export service utilities for PDF and XLSX downloads.
 * Dashboard: html2canvas + jspdf
 * Table: xlsx (existing) or html2canvas + jspdf (Thai-safe; Helvetica cannot render Thai)
 */

import type { jsPDF } from 'jspdf';
import { resolveNativeChartForSection, type PptxNativeChartSpec } from './pptxNativeChart';

/** Excel sheet name max length; invalid chars: \ / * ? : [ ] */
export function sanitizeExcelSheetName(raw: string): string {
  const stripped = raw.replace(/[\\/*?:[\]]/g, '').trim();
  const base = stripped.length ? stripped : 'Sheet1';
  // Use code points — rare surrogate pairs can make .slice(0, 31) wrong vs SheetJS checks
  const pts = Array.from(base);
  if (pts.length <= 31) return pts.join('');
  return pts.slice(0, 31).join('');
}

export type DashboardPdfOptions = {
  /** Default `landscape` (matches previous behavior). */
  orientation?: 'portrait' | 'landscape';
  /** Page padding (mm). Default 8mm. */
  marginMm?: number;
  /**
   * How long to wait for the dashboard to finish loading/refreshing before capture.
   * If it still looks busy, we throw so UI can show a clear error instead of exporting a half-loaded PDF.
   */
  waitForStableMs?: number;
};

const UNSUPPORTED_CANVAS_COLOR_FUNCTION = /\b(?:oklch|oklab|lch|lab)\(/i;

/** Match oklch()/oklab()/lch()/lab() including optional alpha (e.g. `oklch(1 0 0 / 10%)`). */
const UNSUPPORTED_CANVAS_COLOR_FUNCTION_GLOBAL =
  /(?:oklch|oklab|lch|lab)\((?:[^()]|\([^()]*\))*\)/gi;

const CANVAS_COLOR_FALLBACKS = [
  ['color', '#0f172a'],
  ['background-color', '#ffffff'],
  ['border-color', '#e2e8f0'],
  ['border-top-color', '#e2e8f0'],
  ['border-right-color', '#e2e8f0'],
  ['border-bottom-color', '#e2e8f0'],
  ['border-left-color', '#e2e8f0'],
  ['outline-color', '#64748b'],
  ['text-decoration-color', '#0f172a'],
  ['caret-color', '#0f172a'],
  ['fill', '#4f46e5'],
  ['stroke', '#334155'],
  ['stop-color', '#4f46e5'],
  ['flood-color', '#ffffff'],
  ['lighting-color', '#ffffff'],
] as const;

const CANVAS_COMPLEX_COLOR_FALLBACKS = [
  ['background-image', 'none'],
  ['box-shadow', 'none'],
  ['text-shadow', 'none'],
  ['filter', 'none'],
] as const;

const SHADCN_CSS_VAR_HEX_FALLBACKS: Record<string, string> = {
  '--background': '#ffffff',
  '--foreground': '#0f172a',
  '--card': '#ffffff',
  '--card-foreground': '#0f172a',
  '--popover': '#ffffff',
  '--popover-foreground': '#0f172a',
  '--primary': '#0f172a',
  '--primary-foreground': '#f8fafc',
  '--secondary': '#f1f5f9',
  '--secondary-foreground': '#0f172a',
  '--muted': '#f1f5f9',
  '--muted-foreground': '#64748b',
  '--accent': '#f1f5f9',
  '--accent-foreground': '#0f172a',
  '--destructive': '#dc2626',
  '--border': '#e2e8f0',
  '--input': '#e2e8f0',
  '--ring': '#94a3b8',
  '--chart-1': '#cbd5e1',
  '--chart-2': '#64748b',
  '--chart-3': '#475569',
  '--chart-4': '#334155',
  '--chart-5': '#1e293b',
  '--sidebar': '#f8fafc',
  '--sidebar-foreground': '#0f172a',
  '--sidebar-primary': '#0f172a',
  '--sidebar-primary-foreground': '#f8fafc',
  '--sidebar-accent': '#f1f5f9',
  '--sidebar-accent-foreground': '#0f172a',
  '--sidebar-border': '#e2e8f0',
  '--sidebar-ring': '#94a3b8',
};

function hasUnsupportedCanvasColor(value: string | null | undefined): boolean {
  return typeof value === 'string' && UNSUPPORTED_CANVAS_COLOR_FUNCTION.test(value);
}

export function replaceUnsupportedColorFunctionsInCss(cssText: string): string {
  return cssText.replace(UNSUPPORTED_CANVAS_COLOR_FUNCTION_GLOBAL, '#64748b');
}

function cssPropertyFallback(property: string): string {
  if (property.startsWith('--')) {
    return SHADCN_CSS_VAR_HEX_FALLBACKS[property] ?? '#64748b';
  }
  const lower = property.toLowerCase();
  if (lower.includes('background')) return '#ffffff';
  if (lower.includes('foreground') || lower === 'color') return '#0f172a';
  if (lower.includes('border')) return '#e2e8f0';
  if (lower.includes('shadow')) return 'none';
  if (lower.includes('fill')) return '#4f46e5';
  if (lower.includes('stroke')) return '#334155';
  return '#64748b';
}

type CaptureVisibilitySnapshot = {
  opacity: string;
  visibility: string;
  position: string;
  left: string;
  top: string;
  zIndex: string;
  pointerEvents: string;
};

function snapshotCaptureVisibility(element: HTMLElement): CaptureVisibilitySnapshot {
  return {
    opacity: element.style.opacity,
    visibility: element.style.visibility,
    position: element.style.position,
    left: element.style.left,
    top: element.style.top,
    zIndex: element.style.zIndex,
    pointerEvents: element.style.pointerEvents,
  };
}

function applyCaptureVisibility(element: HTMLElement): void {
  element.style.opacity = '1';
  element.style.visibility = 'visible';
  element.style.position = 'fixed';
  element.style.left = '0';
  element.style.top = '0';
  element.style.zIndex = '-1';
  element.style.pointerEvents = 'none';
}

function restoreCaptureVisibility(element: HTMLElement, snapshot: CaptureVisibilitySnapshot): void {
  element.style.opacity = snapshot.opacity;
  element.style.visibility = snapshot.visibility;
  element.style.position = snapshot.position;
  element.style.left = snapshot.left;
  element.style.top = snapshot.top;
  element.style.zIndex = snapshot.zIndex;
  element.style.pointerEvents = snapshot.pointerEvents;
}

async function waitForNextPaint(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function makeNodeVisibleForCapture(node: Element): void {
  if (!('style' in node)) return;
  const style = (node as HTMLElement).style;
  style.setProperty('opacity', '1', 'important');
  style.setProperty('visibility', 'visible', 'important');
}

function elementCaptureHeight(node: HTMLElement): number {
  return Math.max(node.offsetHeight, node.scrollHeight, node.clientHeight);
}

function containsRecharts(node: HTMLElement): boolean {
  return (
    node.classList.contains('recharts-responsive-container') ||
    node.querySelector('.recharts-responsive-container') != null
  );
}

function isBareRechartsContainer(node: HTMLElement): boolean {
  return node.classList.contains('recharts-responsive-container') && node.children.length === 0;
}

function isChartCardElement(node: HTMLElement): boolean {
  return (
    containsRecharts(node) &&
    (node.classList.contains('rounded-xl') ||
      node.classList.contains('rounded-lg') ||
      node.hasAttribute('data-pdf-export-section'))
  );
}

function directChartChildren(node: HTMLElement): HTMLElement[] {
  return Array.from(node.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement && containsRecharts(child),
  );
}

function collectCaptureSections(node: HTMLElement, sections: HTMLElement[]): void {
  if (isChartCardElement(node)) {
    sections.push(node);
    return;
  }

  const splitCharts = directChartChildren(node);
  if (splitCharts.length > 1) {
    splitCharts.forEach((child) => collectCaptureSections(child, sections));
    return;
  }

  const childElements = Array.from(node.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement,
  );

  if (childElements.length > 1) {
    childElements.forEach((child) => collectCaptureSections(child, sections));
    return;
  }

  if (childElements.length === 1) {
    const only = childElements[0];
    if (isBareRechartsContainer(only) && (elementCaptureHeight(node) >= 24 || containsRecharts(node))) {
      sections.push(node);
      return;
    }
    collectCaptureSections(only, sections);
    return;
  }

  if (elementCaptureHeight(node) >= 24 || containsRecharts(node)) {
    sections.push(node);
  }
}

/** Split dashboard into capture blocks so Recharts cards and tables are not one giant bitmap. */
export function findPdfCaptureSections(root: HTMLElement): HTMLElement[] {
  const explicit = Array.from(root.querySelectorAll<HTMLElement>('[data-pdf-export-section]'));
  if (explicit.length > 0) return explicit;

  const sections: HTMLElement[] = [];
  const childElements = Array.from(root.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement,
  );

  if (childElements.length > 1) {
    childElements.forEach((child) => collectCaptureSections(child, sections));
  } else if (childElements.length === 1) {
    collectCaptureSections(childElements[0], sections);
  } else {
    collectCaptureSections(root, sections);
  }

  return sections.length > 0 ? sections : [root];
}

function applySectionCaptureLayout(section: HTMLElement): void {
  const width = Math.max(section.scrollWidth, section.getBoundingClientRect().width, 320);
  section.style.setProperty('width', `${Math.round(width)}px`, 'important');
  section.style.setProperty('max-width', `${Math.round(width)}px`, 'important');
  section.style.setProperty('overflow', 'hidden', 'important');
  section.style.setProperty('isolation', 'isolate', 'important');
  section.style.setProperty('position', 'relative', 'important');
  section.style.setProperty('background-color', '#ffffff', 'important');
  section.style.setProperty('box-sizing', 'border-box', 'important');
}

function stabilizeRechartsForCapture(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.recharts-responsive-container').forEach((container) => {
    const parentCard = container.closest('.rounded-xl, .rounded-lg') as HTMLElement | null;
    const widthSource = parentCard ?? container.parentElement ?? container;
    const width = Math.max(
      container.getBoundingClientRect().width,
      widthSource?.getBoundingClientRect().width ?? 0,
      container.parentElement?.clientWidth ?? 0,
    );
    const height = Math.max(container.getBoundingClientRect().height, container.parentElement?.clientHeight ?? 0);
    if (width > 0) container.style.width = `${Math.round(width)}px`;
    if (height > 0) container.style.height = `${Math.max(Math.round(height), 120)}px`;
  });

  root.querySelectorAll<HTMLElement>('.recharts-wrapper, .recharts-surface').forEach((wrapper) => {
    wrapper.style.overflow = 'hidden';
  });

  root.querySelectorAll('svg.recharts-surface').forEach((svg) => {
    const rect = svg.getBoundingClientRect();
    if (rect.width > 0) svg.setAttribute('width', String(Math.round(rect.width)));
    if (rect.height > 0) svg.setAttribute('height', String(Math.round(rect.height)));
    svg.style.overflow = 'hidden';
  });
}

export function prepareHtml2CanvasClone(_clonedDocument: Document, clonedElement: HTMLElement): void {
  makeNodeVisibleForCapture(clonedElement);

  clonedElement.querySelectorAll<HTMLElement>('.recharts-tooltip-wrapper').forEach((node) => {
    node.style.display = 'none';
  });

  clonedElement.querySelectorAll<HTMLElement>('[aria-busy="true"]').forEach((node) => {
    const className = node.className;
    if (typeof className === 'string' && (className.includes('absolute') || className.includes('fixed'))) {
      node.style.display = 'none';
    }
  });

  stabilizeRechartsForCapture(clonedElement);
}

const DASHBOARD_CAPTURE_SCALE = 2;

export type DashboardSectionCapture = {
  /** PNG data URL (`image/png;base64,...`) for PPTX / downloaders. */
  dataUrl: string;
  label: string;
  widthPx: number;
  heightPx: number;
  canvas: HTMLCanvasElement;
  /** Opt-in editable PowerPoint chart (from `data-pptx-native-chart`). */
  nativeChart?: PptxNativeChartSpec | null;
};

function sectionCaptureLabel(section: HTMLElement, index: number): string {
  const fromAttr = section.getAttribute('data-pdf-export-title')?.trim();
  if (fromAttr) return fromAttr.slice(0, 120);
  const heading = section.querySelector('h1, h2, h3, h4, [data-pdf-export-title]');
  const fromHeading = heading?.textContent?.replace(/\s+/g, ' ').trim();
  if (fromHeading) return fromHeading.slice(0, 120);
  return `แผนภูมิ ${index + 1}`;
}

async function captureHtml2CanvasSection(
  section: HTMLElement,
  html2canvas: (element: HTMLElement, options?: Record<string, unknown>) => Promise<HTMLCanvasElement>,
): Promise<HTMLCanvasElement> {
  applySectionCaptureLayout(section);
  stabilizeRechartsForCapture(section);
  const captureWidth = Math.max(section.scrollWidth, section.getBoundingClientRect().width, 320);
  const captureHeight = Math.max(section.scrollHeight, section.getBoundingClientRect().height, 120);

  return html2canvas(section, {
    scale: DASHBOARD_CAPTURE_SCALE,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: captureWidth,
    height: captureHeight,
    windowWidth: captureWidth,
    windowHeight: captureHeight,
    onclone: (clonedDocument: Document, clonedElement: HTMLElement) => {
      prepareHtml2CanvasClone(clonedDocument, clonedElement);
      applySectionCaptureLayout(clonedElement);
      stabilizeRechartsForCapture(clonedElement);
    },
    scrollX: 0,
    scrollY: 0,
  });
}

/**
 * Wait for a stable dashboard, then capture each chart/section as a PNG canvas.
 * Shared by PDF (fit-one-page) and PPTX (one chart per slide).
 */
export async function captureDashboardSections(
  element: HTMLElement,
  options?: DashboardPdfOptions,
): Promise<DashboardSectionCapture[]> {
  const { default: html2canvas } = await import('html2canvas-pro');

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready;
  }

  const waitMs = options?.waitForStableMs ?? DEFAULT_DASHBOARD_STABLE_WAIT_MS;
  await waitForDashboardStable(element, waitMs);

  const visibilitySnapshot = snapshotCaptureVisibility(element);
  applyCaptureVisibility(element);
  try {
    await waitForNextPaint();
    stabilizeRechartsForCapture(element);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('resize'));
    }
    await waitForNextPaint();

    const sections = findPdfCaptureSections(element);
    const captures: DashboardSectionCapture[] = [];
    for (let index = 0; index < sections.length; index += 1) {
      const section = sections[index];
      const canvas = await captureHtml2CanvasSection(section, html2canvas);
      captures.push({
        canvas,
        dataUrl: canvas.toDataURL('image/png'),
        label: sectionCaptureLabel(section, index),
        widthPx: canvas.width,
        heightPx: canvas.height,
        nativeChart: resolveNativeChartForSection(section),
      });
    }
    return captures;
  } finally {
    restoreCaptureVisibility(element, visibilitySnapshot);
  }
}

function applyCanvasColorFallbacks(element: Element, ownerWindow: Window | null): void {
  if (!('style' in element)) return;
  const style = (element as HTMLElement | SVGElement).style;
  const computed = ownerWindow?.getComputedStyle(element);
  if (!computed) return;

  for (let index = 0; index < computed.length; index += 1) {
    const property = computed.item(index);
    const computedValue = computed.getPropertyValue(property);
    const inlineValue = style.getPropertyValue(property);
    if (hasUnsupportedCanvasColor(computedValue) || hasUnsupportedCanvasColor(inlineValue)) {
      style.setProperty(property, cssPropertyFallback(property), 'important');
    }
  }

  for (const [property, fallback] of CANVAS_COLOR_FALLBACKS) {
    const computedValue = computed.getPropertyValue(property);
    const inlineValue = style.getPropertyValue(property);
    if (hasUnsupportedCanvasColor(computedValue) || hasUnsupportedCanvasColor(inlineValue)) {
      style.setProperty(property, fallback, 'important');
    }
  }

  for (const [property, fallback] of CANVAS_COMPLEX_COLOR_FALLBACKS) {
    const computedValue = computed.getPropertyValue(property);
    const inlineValue = style.getPropertyValue(property);
    if (hasUnsupportedCanvasColor(computedValue) || hasUnsupportedCanvasColor(inlineValue)) {
      style.setProperty(property, fallback, 'important');
    }
  }
}

export function sanitizeUnsupportedCanvasColors(root: ParentNode, ownerWindow?: Window | null): void {
  const view =
    ownerWindow ??
    (root instanceof Document
      ? root.defaultView
      : root instanceof Element
        ? root.ownerDocument.defaultView
        : null);
  const elements: Element[] = root instanceof Element ? [root] : [];
  root.querySelectorAll('*').forEach((element) => elements.push(element));
  elements.forEach((element) => applyCanvasColorFallbacks(element, view));
}

export type FitCanvasToPageResult = {
  drawWidthMm: number;
  drawHeightMm: number;
  drawXMm: number;
  drawYMm: number;
  printableWidthMm: number;
  printableHeightMm: number;
};

/**
 * Contain/fit canvas pixel size into the printable page area (margins applied).
 * Maximizes draw size while preserving aspect ratio; centers on the page by default.
 */
export function fitCanvasToPage(
  canvasWidthPx: number,
  canvasHeightPx: number,
  pageWidthMm: number,
  pageHeightMm: number,
  marginMm: number,
  options?: { verticalAlign?: 'center' | 'top' },
): FitCanvasToPageResult {
  const safeMargin = Number.isFinite(marginMm) ? Math.max(0, marginMm) : 0;
  const printableWidthMm = Math.max(10, pageWidthMm - safeMargin * 2);
  const printableHeightMm = Math.max(10, pageHeightMm - safeMargin * 2);

  if (!(canvasWidthPx > 0) || !(canvasHeightPx > 0)) {
    return {
      drawWidthMm: printableWidthMm,
      drawHeightMm: printableHeightMm,
      drawXMm: safeMargin,
      drawYMm: safeMargin,
      printableWidthMm,
      printableHeightMm,
    };
  }

  const scale = Math.min(printableWidthMm / canvasWidthPx, printableHeightMm / canvasHeightPx);
  const drawWidthMm = canvasWidthPx * scale;
  const drawHeightMm = canvasHeightPx * scale;
  const drawXMm = safeMargin + (printableWidthMm - drawWidthMm) / 2;
  const verticalAlign = options?.verticalAlign ?? 'center';
  const drawYMm =
    verticalAlign === 'top'
      ? safeMargin
      : safeMargin + (printableHeightMm - drawHeightMm) / 2;

  return {
    drawWidthMm,
    drawHeightMm,
    drawXMm,
    drawYMm,
    printableWidthMm,
    printableHeightMm,
  };
}

/**
 * Place an entire canvas on a single PDF page (contain/fit). Never slices across pages.
 * Used by dashboard chart/section export.
 */
export function addCanvasFitOnePage(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  imageFormat: 'PNG' | 'JPEG',
  marginMm: number,
  options?: { addLeadingPage?: boolean; verticalAlign?: 'center' | 'top' },
): void {
  if (options?.addLeadingPage) {
    pdf.addPage();
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const fit = fitCanvasToPage(
    canvas.width,
    canvas.height,
    pageWidth,
    pageHeight,
    marginMm,
    { verticalAlign: options?.verticalAlign ?? 'center' },
  );

  const imgData =
    imageFormat === 'PNG'
      ? canvas.toDataURL('image/png')
      : canvas.toDataURL('image/jpeg', 0.92);

  pdf.addImage(
    imgData,
    imageFormat,
    fit.drawXMm,
    fit.drawYMm,
    fit.drawWidthMm,
    fit.drawHeightMm,
  );
}

/** Fit-to-width; tall canvases are cropped per page (avoids duplicated slices from image offset). */
export function addCanvasPaginatedFullWidth(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  imageFormat: 'PNG' | 'JPEG',
  marginMm: number,
  options?: { addLeadingPage?: boolean },
): void {
  const fmt = imageFormat;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const safeMargin = Number.isFinite(marginMm) ? Math.max(0, marginMm) : 0;
  const drawX = safeMargin;
  const drawY = safeMargin;
  const drawWidth = Math.max(10, pageWidth - safeMargin * 2);
  const drawHeight = Math.max(10, pageHeight - safeMargin * 2);
  const sliceHeightPx = Math.max(1, Math.floor((drawHeight * canvas.width) / drawWidth));

  let sourceY = 0;
  let pageIndex = 0;

  while (sourceY < canvas.height) {
    if (pageIndex > 0 || options?.addLeadingPage) {
      pdf.addPage();
    }
    pageIndex += 1;

    const remainingPx = canvas.height - sourceY;
    const currentSlicePx = Math.min(sliceHeightPx, remainingPx);

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = currentSlicePx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D unavailable for PDF export');
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      currentSlicePx,
      0,
      0,
      canvas.width,
      currentSlicePx,
    );

    const imgData =
      imageFormat === 'PNG'
        ? sliceCanvas.toDataURL('image/png')
        : sliceCanvas.toDataURL('image/jpeg', 0.92);
    const sliceHeightMm = (currentSlicePx * drawWidth) / canvas.width;

    pdf.addImage(imgData, fmt, drawX, drawY, drawWidth, sliceHeightMm);
    sourceY += currentSlicePx;
  }
}

function looksLikeLoading(root: HTMLElement): boolean {
  // Common patterns in this app:
  // - aria-busy / role="status"
  // - animated spinner utility class
  // - Thai loading strings for refresh
  if (root.querySelector('[aria-busy="true"]')) return true;
  if (root.querySelector('[role="status"]')) {
    const busyStatus = root.querySelector('[role="status"][aria-busy="true"]');
    if (busyStatus) return true;
  }
  if (root.querySelector('.animate-spin')) return true;
  const text = root.innerText || '';
  if (text.includes('กำลังอัปเดตข้อมูล') || text.includes('กำลังโหลดข้อมูล') || text.includes('กำลังโหลด')) return true;
  return false;
}

export class ExportDashboardStillLoadingError extends Error {
  readonly code = 'EXPORT_DASHBOARD_STILL_LOADING';

  constructor() {
    super('Dashboard is still loading');
    this.name = 'ExportDashboardStillLoadingError';
  }
}

export function isExportDashboardStillLoadingError(error: unknown): boolean {
  return (
    error instanceof ExportDashboardStillLoadingError ||
    (error instanceof Error && error.message.toLowerCase().includes('still loading'))
  );
}

const DEFAULT_DASHBOARD_STABLE_WAIT_MS = 120_000;

async function waitForDashboardStable(root: HTMLElement, timeoutMs: number): Promise<void> {
  if (timeoutMs <= 0) return;
  const start = Date.now();
  // Require two consecutive checks to be stable to avoid capturing between frames.
  let stableCount = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const busy = looksLikeLoading(root);
    if (!busy) stableCount += 1;
    else stableCount = 0;
    if (stableCount >= 2) return;
    if (Date.now() - start > timeoutMs) {
      throw new ExportDashboardStillLoadingError();
    }
    await new Promise((r) => setTimeout(r, 250));
  }
}

/**
 * Capture a DOM element and export it as a PDF file (A4).
 * Each capture section (chart card) is placed on exactly one page via contain/fit scaling.
 */
export async function exportDashboardToPdf(
  element: HTMLElement,
  filename: string,
  options?: DashboardPdfOptions,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const captures = await captureDashboardSections(element, options);
  const orientation = options?.orientation ?? 'landscape';
  const marginMm = options?.marginMm ?? 8;
  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
  });

  for (let index = 0; index < captures.length; index += 1) {
    addCanvasFitOnePage(pdf, captures[index].canvas, 'PNG', marginMm, {
      addLeadingPage: index > 0,
    });
  }

  pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

/**
 * Export table data to XLSX file.
 * Follows existing pattern from SnCompletionMatrix and TGProjectPage.
 */
export async function exportTableToXlsx(
  sheetRows: string[][],
  sheetName: string,
  filename: string
): Promise<void> {
  const xlsx = await import('xlsx');
  const worksheet = xlsx.utils.aoa_to_sheet(sheetRows);
  const workbook = xlsx.utils.book_new();
  const safeName = sanitizeExcelSheetName(sheetName);
  xlsx.utils.book_append_sheet(workbook, worksheet, safeName);
  xlsx.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

export type ExportCellValue = string | number | boolean | Date | null;

export type ExportWorkbookSheet = {
  sheetName: string;
  rows: ExportCellValue[][];
  columnWidths?: number[];
  autoFilter?: boolean;
  freezeHeaderRows?: number;
};

function uniqueExcelSheetName(rawName: string, usedNames: Set<string>, index: number): string {
  const baseName = sanitizeExcelSheetName(rawName || `Sheet${index + 1}`);
  let safeName = baseName;
  let suffix = 2;
  while (usedNames.has(safeName)) {
    const suffixText = ` ${suffix}`;
    const basePrefix = Array.from(baseName).slice(0, 31 - Array.from(suffixText).length).join('');
    safeName = sanitizeExcelSheetName(`${basePrefix}${suffixText}`);
    suffix += 1;
  }
  usedNames.add(safeName);
  return safeName;
}

function columnName(index: number): string {
  let n = index + 1;
  let name = '';
  while (n > 0) {
    const remainder = (n - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function applyWorksheetDefaults(
  worksheet: Record<string, unknown>,
  rows: ExportCellValue[][],
  sheet: ExportWorkbookSheet,
): void {
  const maxCols = rows.reduce((max, row) => Math.max(max, row.length), 0);
  worksheet['!cols'] = Array.from({ length: maxCols }, (_value, index) => {
    const configured = sheet.columnWidths?.[index];
    if (configured != null && Number.isFinite(configured)) {
      return { wch: Math.min(60, Math.max(8, configured)) };
    }
    const contentWidth = rows.reduce((max, row) => {
      const value = row[index];
      return Math.max(max, value == null ? 0 : Array.from(String(value)).length);
    }, 0);
    return { wch: Math.min(42, Math.max(10, contentWidth + 2)) };
  });

  if (sheet.autoFilter && rows.length > 0 && maxCols > 0) {
    worksheet['!autofilter'] = { ref: `A1:${columnName(maxCols - 1)}${rows.length}` };
  }

  if (sheet.freezeHeaderRows && sheet.freezeHeaderRows > 0) {
    worksheet['!freeze'] = {
      xSplit: 0,
      ySplit: sheet.freezeHeaderRows,
      topLeftCell: `A${sheet.freezeHeaderRows + 1}`,
      activePane: 'bottomLeft',
      state: 'frozen',
    };
  }
}

export async function exportWorkbookToXlsx(
  sheets: ExportWorkbookSheet[],
  filename: string,
): Promise<void> {
  const xlsx = await import('xlsx');
  const workbook = xlsx.utils.book_new();
  const usedSheetNames = new Set<string>();
  const exportSheets: ExportWorkbookSheet[] =
    sheets.length > 0
      ? sheets
      : [{ sheetName: 'คำอธิบาย', rows: [['ไม่มีข้อมูลสำหรับส่งออก']] }];

  exportSheets.forEach((sheet, index) => {
    const worksheet = xlsx.utils.aoa_to_sheet(sheet.rows);
    applyWorksheetDefaults(worksheet as Record<string, unknown>, sheet.rows, sheet);
    const safeName = uniqueExcelSheetName(sheet.sheetName, usedSheetNames, index);
    xlsx.utils.book_append_sheet(workbook, worksheet, safeName);
  });

  xlsx.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

const PDF_TABLE_FONT_STACK = `"Google Sans", "Noto Sans Thai", "Leelawadee UI", Tahoma, "DejaVu Sans", sans-serif`;

/** ~rows per html2canvas capture so one landscape page usually fits; very tall tables split across PDF pages inside the canvas. */
const PDF_TABLE_ROWS_PER_CHUNK = 28;

function buildTablePdfDom(
  headers: string[],
  rows: string[][],
  metaRows?: string[][],
): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'background:#ffffff',
    'padding:14px',
    'box-sizing:border-box',
    'width:1800px',
    `font-family:${PDF_TABLE_FONT_STACK}`,
    'color:#0f172a',
  ].join(';');

  if (metaRows?.length) {
    const meta = document.createElement('div');
    meta.style.cssText = 'margin-bottom:12px;font-size:13px;line-height:1.45;';
    metaRows.forEach((pair) => {
      const line = document.createElement('div');
      line.textContent = `${pair[0]}: ${pair[1]}`;
      meta.appendChild(line);
    });
    wrap.appendChild(meta);
  }

  const table = document.createElement('table');
  table.style.cssText = 'border-collapse:collapse;width:100%;font-size:11px;table-layout:fixed;';

  const thead = document.createElement('thead');
  const trh = document.createElement('tr');
  headers.forEach((h) => {
    const th = document.createElement('th');
    th.textContent = h;
    th.style.cssText =
      'border:1px solid #334155;background:#475569;color:#fff;padding:6px 8px;text-align:left;font-weight:600;word-break:break-word;';
    trh.appendChild(th);
  });
  thead.appendChild(trh);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  rows.forEach((row) => {
    const tr = document.createElement('tr');
    row.forEach((cell) => {
      const td = document.createElement('td');
      td.textContent = cell;
      td.style.cssText = 'border:1px solid #cbd5e1;padding:4px 6px;vertical-align:top;word-break:break-word;';
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);
  wrap.appendChild(table);
  return wrap;
}


/**
 * Export table data to PDF using html2canvas (Thai / Unicode) + jsPDF.
 * Avoids Helvetica/gibberish from jspdf-autotable for non-Latin text.
 */
export async function exportTableToPdf(
  headers: string[],
  rows: string[][],
  filename: string,
  metaRows?: string[][]
): Promise<void> {
  const { default: html2canvas } = await import('html2canvas-pro');
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready;
  }

  const chunks: string[][][] = [];
  for (let i = 0; i < rows.length; i += PDF_TABLE_ROWS_PER_CHUNK) {
    chunks.push(rows.slice(i, i + PDF_TABLE_ROWS_PER_CHUNK));
  }
  if (chunks.length === 0) {
    chunks.push([]);
  }

  let first = true;
  for (const chunk of chunks) {
    const el = buildTablePdfDom(headers, chunk, first ? metaRows : undefined);
    el.style.position = 'fixed';
    el.style.left = '-12000px';
    el.style.top = '0';
    document.body.appendChild(el);
    try {
      const canvas = await html2canvas(el, {
        scale: 1.35,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        onclone: (clonedDocument, clonedElement) => {
          prepareHtml2CanvasClone(clonedDocument, clonedElement);
        },
      });
      if (!first) pdf.addPage();
      addCanvasPaginatedFullWidth(pdf, canvas, 'JPEG', 8);
      first = false;
    } finally {
      document.body.removeChild(el);
    }
  }

  pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}
